/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";
import { useTranslation } from "react-i18next";


interface BatchSidebarSectionProps {
  batchDetails: any;
}

const BatchSidebarSection: React.FC<BatchSidebarSectionProps> = ({
  batchDetails,
}) => {
  const { t } = useTranslation();
  const dispensingSummary = batchDetails?.dispensing_summary;
  const totalYield = dispensingSummary?.total_dispensed_volume_ml || "-";
  const expectedYield = dispensingSummary?.total_prepared_volume_ml || "-";
  const deviationCount = dispensingSummary?.remaining_units || "-";

  const yieldPercentage =
  Number(expectedYield) > 0
    ? ((Number(totalYield) / Number(expectedYield)) * 100).toFixed(2)
    : "-";


  const status = (batchDetails?.status || "").toLowerCase();
  const currentStep =
    status === "draft"
      ? 2
      : status === "in preparation"
      ? 3
      : status === "sterilized"
      ? 5
      : status === "media dispensed"
      ? 6
      : status === "fine tuned"
      ? 7
      : status === "ready to use" || status === "rejected"
      ? 8
      : 1;
  const steps = [
    { n: 1, label: t("mediaPrepBatch.mediaPrepBatchDetails.BatchCreation") },
    { n: 2, label: t("mediaPrepBatch.mediaPrepBatchDetails.MediaLotDetails") },
    { n: 3, label: t("mediaPrepBatch.mediaPrepBatchDetails.Preparation") },
    { n: 4, label: t("mediaPrepBatch.mediaPrepBatchDetails.Sterilization") },
    { n: 5, label: t("mediaPrepBatch.mediaPrepBatchDetails.Dispensing") },
    { n: 6, label: t("mediaPrepBatch.mediaPrepBatchDetails.FineTuning") },
    { n: 7, label: t("mediaPrepBatch.mediaPrepBatchDetails.Validationsidebar") },
  ];

  return (
    <div className="lg:col-span-3 space-y-4">
      {/* Process Summary */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-0.5 border-b border-slate-200">
            <h4 className="text-[12px] font-bold uppercase tracking-wide text-[#1E2B4D] mt-[9px]">{t("mediaPrepBatch.mediaPrepBatchDetails.ProcessSummary")}</h4>
          </div>

          <div className="px-4 py-2">
            {steps.map((step, index, arr) => {
              const completed = step.n < currentStep;
              const active = step.n === currentStep && currentStep !== 8;
              return (
              <div key={step.n} className="relative flex items-center justify-between py-3">
              {index !== arr.length - 1 && (
                <div
                  className={`absolute left-[12px] top-[28px] h-[45px] w-[2px] z-0 ${
                    step.n < currentStep ? "bg-[#1E8E3E]" : "bg-[#D9D9D9]"
                  }`}
                />
              )}

              <div className="flex items-center gap-3 z-10">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    completed
                      ? "bg-[#1E8E3E] text-white"
                      : active
                      ? "bg-[#2F80ED] text-white"
                      : "bg-[#E5E7EB] text-[#6B7280]"
                  }`}
                >
                  {step.n}
                </div>
                <span className="text-[11.5px] font-bold uppercase tracking-wide text-[#1E2B4D] whitespace-nowrap">{step.label}</span>
              </div>

              {completed && (
                <span
                  className="material-symbols-outlined text-[20px] text-[#1E8E3E]"
                  style={{ fontVariationSettings: "'FILL' 1,'wght' 400,'GRAD' 0,'opsz' 24" }}
                >
                  check_circle
                </span>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Batch Metrics */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex bg-[#F8FAFC] items-center justify-between px-3 py-2 border-b border-slate-200" style={{ borderBottom: "1px solid #D6DEE8" }}>
          <h4 className="text-[12px] font-bold uppercase tracking-wide text-[#1E2B4D] mt-[9px]">{t("mediaPrepBatch.mediaPrepBatchDetails.BatchMetrics")}</h4>
          <span className="material-symbols-outlined text-[#F97316] text-[22px]">{t("mediaPrepBatch.mediaPrepBatchDetails.monitoring")}</span>
        </div>

        {/* Body */}
        <div className="px-3 bg-[#F8FAFC]">
          <div className="flex justify-between items-center py-3 border-b border-slate-200" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <span className="text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.TOTAL YIELD")}</span>
            <span className="text-[14px] font-semibold text-[#1E2B4D]">{totalYield} mL </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-slate-200" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <span className="text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.EXPECTED YIELD")}</span>
            <span className="text-[14px] font-semibold text-[#1E2B4D]">{expectedYield} mL </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-slate-200" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <span className="text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.YIELD")}</span>
            <span className="text-[14px] font-bold text-green-600">{yieldPercentage !== "-" ? `${yieldPercentage}%` : "-"}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-slate-200" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <span className="text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.WASTE")}</span>
            <span className="text-[14px] font-semibold text-[#1E2B4D]">0 mL</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-slate-200" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <span className="text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.DEVIATION COUNT")}</span>
            <span className="text-[14px] font-semibold text-[#1E2B4D]">{deviationCount}</span>
          </div>

          <div className="flex justify-between items-center py-3" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <span className="text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.BATCH STATUS")}</span>
            <span
              className={`px-3 py-1 rounded text-[11px] font-bold uppercase
              ${
                status === "ready to use"
                  ? "bg-green-100 text-green-700"
                  : status === "rejected"
                  ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
              }`}
            >
              {batchDetails?.status}
            </span>
          </div>
        </div>
      </div>


     {/* Quick Actions */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div
          className="bg-[#081D45] px-3 py-2 border-b border-slate-200"
          style={{ borderBottom: "1px solid #D6DEE8" }}
        >
          <h4 className="text-[12px] font-bold uppercase tracking-wide text-white mt-[9px]">
            {t("mediaPrepBatch.mediaPrepBatchDetails.QUICK ACTIONS")}
          </h4>
        </div>
        <div className="p-3 space-y-4">
          <button
            className="w-full flex items-center justify-between px-4 py-4 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition"
            onClick={() => {
              // Print Label Action
            }}
          >
            <span className="text-[11.5px] font-bold uppercase text-[#1E2B4D]">{t("mediaPrepBatch.mediaPrepBatchDetails.PRINT LABEL")}</span>
            <span className="material-symbols-outlined text-[#1E2B4D] text-[22px]">{t("mediaPrepBatch.mediaPrepBatchDetails.print")}</span>
          </button>

          <button
            className="w-full flex items-center justify-between px-4 py-4 bg-white border border-slate-200 rounded-md hover:bg-red-50 transition"
            onClick={() => {
              // Reject Action
            }}
          >
            <span className="text-[11.5px] font-bold uppercase text-[#DC2626] text-left">{t("mediaPrepBatch.mediaPrepBatchDetails.MARK BATCH")}<br />{t("mediaPrepBatch.mediaPrepBatchDetails.AS REJECTED")}</span>
            <span className="material-symbols-outlined text-[#DC2626] text-[22px]">{t("mediaPrepBatch.mediaPrepBatchDetails.warning")}</span>
          </button>
        </div>
      </div> 
  </div>
    );
};

export default BatchSidebarSection;
